@tailwind base;
@tailwind components;
@tailwind utilities;

/* Design tokens — all overridden at runtime by applyCssVariables() */
:root {
    /* Spacing */
    --space-xs: 4px;
    --space-sm: 8px;
    --space-md: 16px;
    --space-lg: 24px;
    --space-xl: 30px;
    /* Typography */
    --font-xxs: 10px;
    --font-xs: 12px;
    --font-sm: 14px;
    --font-md: 16px;
    --font-lg: 18px;
    --font-xl: 24px;
    /* Buttons */
    --btn-pad-v: 12px;
    --btn-pad-h: 24px;
    --btn-font: 16px;
    /* Layout */
    --header-pad: 16px 24px;
    --card-pad: 24px;
    --main-pad: 0 24px 24px;
    --logo-size: 90px;
    /* Border radius */
    --radius-sm: 4px;
    --radius-md: 8px;
    --radius-lg: 12px;
    --radius-full: 9999px;
    /* Shadows */
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.08);
    --shadow-md: 0 2px 8px rgba(0,0,0,0.10);
    --shadow-lg: 0 4px 16px rgba(0,0,0,0.15);
}

/* Header logo — size driven by --logo-size */
.header-logo svg {
    width: var(--logo-size);
    height: var(--logo-size);
}

/* Custom scrollbar */
@layer utilities {
    .scrollbar-thin::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    .scrollbar-thin::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
    }

    .scrollbar-thin::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
    }

    .scrollbar-thin::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
}

/* Base styles */
@layer base {
    body {
        @apply text-gray-900 antialiased;
    }

    * {
        @apply border-gray-200;
    }
}

/* Restore browser defaults inside the Jodit editor (Tailwind preflight resets them) */
.jodit-wysiwyg h1 { font-size: 2em; font-weight: 700; margin: 0.67em 0; }
.jodit-wysiwyg h2 { font-size: 1.5em; font-weight: 700; margin: 0.83em 0; }
.jodit-wysiwyg h3 { font-size: 1.17em; font-weight: 700; margin: 1em 0; }
.jodit-wysiwyg h4 { font-size: 1em; font-weight: 700; margin: 1.33em 0; }
.jodit-wysiwyg h5 { font-size: 0.83em; font-weight: 700; margin: 1.67em 0; }
.jodit-wysiwyg h6 { font-size: 0.67em; font-weight: 700; margin: 2.33em 0; }
.jodit-wysiwyg ul { list-style-type: disc; padding-left: 2em; margin: 1em 0; }
.jodit-wysiwyg ol { list-style-type: decimal; padding-left: 2em; margin: 1em 0; }
.jodit-wysiwyg a { color: #2563eb; text-decoration: underline; }
.jodit-wysiwyg blockquote { border-left: 4px solid #d1d5db; padding-left: 1em; margin: 1em 0; color: #6b7280; font-style: italic; }
.jodit-wysiwyg table { border-collapse: collapse; width: 100%; margin: 1em 0; }
.jodit-wysiwyg th, .jodit-wysiwyg td { border: 1px solid #d1d5db; padding: 0.5em 0.75em; }
.jodit-wysiwyg th { background-color: #f9fafb; font-weight: 700; }
.jodit-wysiwyg pre { background-color: #f3f4f6; border-radius: 4px; padding: 1em; margin: 1em 0; overflow-x: auto; }
.jodit-wysiwyg code { background-color: #f3f4f6; border-radius: 3px; padding: 0.1em 0.3em; font-family: ui-monospace, monospace; font-size: 0.9em; }
.jodit-wysiwyg pre code { background: none; padding: 0; }
.jodit-wysiwyg hr { border: none; border-top: 1px solid #d1d5db; margin: 1.5em 0; }
.jodit-wysiwyg sub { vertical-align: sub; font-size: 75%; }
.jodit-wysiwyg sup { vertical-align: super; font-size: 75%; }

/* Compact numeric inputs (e.g. layout margin fields) drop the native spin buttons so the
   reclaimed width stays usable for typing digits next to a leading icon */
.no-number-spinner::-webkit-inner-spin-button,
.no-number-spinner::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.no-number-spinner {
    -moz-appearance: textfield;
}

/* Guitar song presentation layout: manual page breaks only apply when printing */
@media print {
    .song-layout-page-break {
        page-break-before: always;
    }
    .song-layout-page-break-indicator {
        display: none;
    }
}

/* On screen, a page break shows as a dashed rule instead of an actual break */
.song-layout-page-break-indicator {
    border: none;
    border-top: 2px dashed currentColor;
    opacity: 0.4;
    margin: 0 0 16px 0;
}

/* SongInlineEditableNumber (SongInlineEditableField.tsx) draws its own +/- step buttons, so the
   browser's native number spinner would otherwise show twice -- once from the browser, once
   from this component. */
.song-number-input::-webkit-outer-spin-button,
.song-number-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.song-number-input {
    -moz-appearance: textfield;
}

/* Custom animations */
@layer utilities {
    .animate-fade-in {
        animation: fadeIn 0.3s ease-in-out;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
}
